import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addNewProduct,
  adjustProductStock,
  getProductHistory,
  reserveProductBarcode,
  updateProduct,
  validateProductBarcode,
} from "../logic/apiCalls";
import { barcodeDataUrl, downloadBarcodeSvg, printBarcodeLabels } from "../logic/barcodes";
import CustomSelect from "../../globalcontrols/customselect/customselect";
import InventoryToast from "./InventoryToast";
import { isWeightedUnit, normalizeSaleUnit, priceUnitLabel, stockUnitLabel } from "../../pos/logic/saleUnits";

const EDIT_TABS = [
  ["general", "Información", "inventory_2"],
  ["classification", "Clasificación", "category"],
  ["pricing", "Precios", "payments"],
  ["inventory", "Inventario", "warehouse"],
  ["barcode", "Código y etiquetas", "barcode"],
  ["history", "Historial", "history"],
];

const CREATE_TABS = [
  ["general", "Producto", "inventory_2"],
  ["classification", "Clasificación", "category"],
  ["pricing", "Precio y stock", "payments"],
  ["barcode", "Código y etiquetas", "barcode"],
  ["review", "Revisar", "fact_check"],
];

const BARCODE_TYPE_OPTIONS = [
  { value: "code128", label: "Code 128 · Uso interno recomendado" },
  { value: "ean13", label: "EAN-13 · Requiere numeración autorizada" },
];

const LABEL_SIZE_OPTIONS = [
  { value: "50x30", label: "50 × 30 mm" },
  { value: "40x25", label: "40 × 25 mm" },
];

function createDraft(product = {}) {
  const source = product || {};
  return {
    code: String(source.code || ""),
    barcode_type: /^\d{13}$/.test(String(source.code || "")) ? "ean13" : "code128",
    barcode_reservation_id: "",
    brand: source.brand || "",
    model: source.model || "",
    flavor: source.flavor || "",
    packaging: source.packaging || "",
    category: source.category || "",
    subcategory: source.subcategory || "",
    format: source.format ?? "",
    format_unit: source.format_unit || "",
    sale_unit: normalizeSaleUnit(source.sale_unit || ""),
    location: source.location ? String(source.location) : "",
    unit_price: source.unit_price ?? 0,
    unit_cost: source.unit_cost ?? 0,
    margin: source.margin ?? 0,
    qty: source.qty ?? 0,
    adjustment_reason: "",
  };
}

function Field({ label, hint, required, children, className = "" }) {
  return (
    <label className={`inventory-form-field ${className}`.trim()}>
      <span className="inventory-form-field__label">{label}{required ? <b aria-hidden="true"> *</b> : null}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function SelectField({ label, hint, required, className = "", ...selectProps }) {
  return (
    <div className={`inventory-form-field ${className}`.trim()}>
      <CustomSelect
        {...selectProps}
        className="inventory-custom-select"
        iosArrow
        label={<>{label}{required ? <b aria-hidden="true"> *</b> : null}</>}
      />
      {hint ? <small>{hint}</small> : null}
    </div>
  );
}

function money(value) {
  return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(Number(value || 0));
}

function ProductEditorModal({ mode = "edit", product, initialTab = "general", availableLocations, categoryStructure, saleUnits, onClose, onSaved }) {
  const isCreate = mode === "create";
  const tabs = isCreate ? CREATE_TABS : EDIT_TABS;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [draft, setDraft] = useState(() => createDraft(product));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [history, setHistory] = useState({ events: [], adjustments: [] });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [labelCopies, setLabelCopies] = useState(1);
  const [labelSize, setLabelSize] = useState("50x30");
  const modalRef = useRef(null);
  const firstTabRef = useRef(null);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const onCloseRef = useRef(onClose);

  const categories = useMemo(() => Object.keys(categoryStructure || {}).sort((a, b) => a.localeCompare(b)), [categoryStructure]);
  const subcategories = useMemo(() => categoryStructure?.[draft.category]?.subcategory || [], [categoryStructure, draft.category]);
  const saleUnitOptions = useMemo(() => [...new Set([
    ...Object.values(saleUnits || {}).map(normalizeSaleUnit),
    "un", "kg", "gr",
  ].filter(Boolean))], [saleUnits]);
  const productName = [draft.brand, draft.model, draft.flavor, draft.format, draft.format_unit].filter(Boolean).join(" ");
  const suggestedPrice = Number(draft.margin) >= 100 ? 0 : Math.round(Number(draft.unit_cost || 0) / (1 - Number(draft.margin || 0) / 100));
  const actualMargin = Number(draft.unit_price) > 0
    ? Math.round(((Number(draft.unit_price) - Number(draft.unit_cost)) / Number(draft.unit_price)) * 1000) / 10
    : 0;
  const weighted = isWeightedUnit(draft.sale_unit);
  const stockStep = normalizeSaleUnit(draft.sale_unit) === "kg" ? "0.001" : "1";
  const unitLabel = stockUnitLabel(draft.sale_unit);
  const dirty = JSON.stringify(draft) !== JSON.stringify(createDraft(product));
  dirtyRef.current = dirty;
  savingRef.current = saving;
  onCloseRef.current = onClose;

  const update = (field, value) => {
    setMessage(null);
    setDraft((current) => ({ ...current, [field]: value }));
  };

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    firstTabRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !savingRef.current) onCloseRef.current(dirtyRef.current);
      if (event.key !== "Tab" || !modalRef.current) return;
      const focusable = [...modalRef.current.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, []);

  useEffect(() => {
    if (activeTab !== "history" || isCreate || historyLoading || historyLoaded) return;
    setHistoryLoading(true);
    getProductHistory(product.id)
      .then((result) => setHistory({ events: result.events || [], adjustments: result.adjustments || [] }))
      .catch((error) => setMessage({ type: "error", text: error.message }))
      .finally(() => { setHistoryLoading(false); setHistoryLoaded(true); });
  }, [activeTab, historyLoaded, historyLoading, isCreate, product]);

  const validateBasics = () => {
    if (!draft.brand.trim() || !draft.model.trim()) return "Completa la marca y el producto/modelo.";
    if (!draft.category || !draft.subcategory || !draft.location) return "Completa categoría, subcategoría y ubicación.";
    if (!draft.code.trim()) return "Ingresa o genera un código de barras.";
    if (Number(draft.margin) < 0 || Number(draft.margin) >= 100) return "El margen debe estar entre 0 y 99,99%.";
    return null;
  };

  const saveProduct = async () => {
    const validationMessage = validateBasics();
    if (validationMessage) {
      setMessage({ type: "error", text: validationMessage });
      return;
    }
    setSaving(true);
    setMessage({ type: "info", text: isCreate ? "Creando producto…" : "Guardando cambios…" });
    try {
      if (isCreate) {
        const result = await addNewProduct({
          ...draft,
          location: Number(draft.location),
          format: Number(draft.format || 0),
          unit_price: Number(draft.unit_price || 0),
          unit_cost: Number(draft.unit_cost || 0),
          margin: Number(draft.margin || 0),
          qty: Number(draft.qty || 0),
        });
        setMessage({ type: "success", text: "Producto creado correctamente." });
        onSaved(result.product, { keepOpen: false });
      } else {
        const result = await updateProduct(product.id, {
          code: draft.code,
          barcode_type: draft.barcode_type,
          barcode_reservation_id: draft.barcode_reservation_id || undefined,
          brand: draft.brand,
          model: draft.model,
          flavor: draft.flavor,
          packaging: draft.packaging,
          category: draft.category,
          subcategory: draft.subcategory,
          format: Number(draft.format || 0),
          format_unit: draft.format_unit,
          sale_unit: draft.sale_unit,
          unit_price: Number(draft.unit_price || 0),
          unit_cost: Number(draft.unit_cost || 0),
          margin: Number(draft.margin || 0),
        }, product.inventory_revision);
        setMessage({ type: "success", text: "Cambios guardados." });
        onSaved(result.product, { keepOpen: false });
      }
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const applyStockAdjustment = async () => {
    if (!draft.adjustment_reason.trim()) {
      setMessage({ type: "error", text: "Indica el motivo del ajuste de inventario." });
      return;
    }
    setSaving(true);
    try {
      const result = await adjustProductStock(product.id, {
        newQuantity: Number(draft.qty),
        reason: draft.adjustment_reason,
        expectedRevision: product.inventory_revision,
      });
      setMessage({ type: "success", text: "Inventario ajustado y registrado en el historial." });
      onSaved(result.product, { keepOpen: true });
      setDraft((current) => ({ ...current, qty: result.product.qty, adjustment_reason: "" }));
      setHistory({ events: [], adjustments: [] });
      setHistoryLoaded(false);
    } catch (error) {
      const suffix = error.details?.currentQuantity !== undefined ? ` Stock actual: ${error.details.currentQuantity}.` : "";
      setMessage({ type: "error", text: `${error.message}${suffix}` });
    } finally {
      setSaving(false);
    }
  };

  const generateBarcode = async () => {
    if (draft.barcode_type === "ean13") {
      setMessage({ type: "info", text: "EAN-13 debe provenir del rango autorizado de la empresa. Para códigos internos generados usa Code 128." });
      return;
    }
    setSaving(true);
    try {
      const result = await reserveProductBarcode(draft.barcode_type);
      setDraft((current) => ({ ...current, code: result.reservation.barcode, barcode_reservation_id: result.reservation.id }));
      setMessage({ type: "success", text: "Código reservado durante 20 minutos." });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const checkBarcode = async () => {
    setSaving(true);
    try {
      const result = await validateProductBarcode({ barcode: draft.code, barcodeType: draft.barcode_type, location: draft.location });
      setMessage({ type: result.available || result.existingProduct?.id === product?.id ? "success" : "error", text: result.available || result.existingProduct?.id === product?.id ? "El código es válido y está disponible." : "Este código ya está asignado a otro producto." });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  let barcodeUrl = "";
  try {
    if (draft.code) barcodeUrl = barcodeDataUrl({ value: draft.code, type: draft.barcode_type, productName, price: money(draft.unit_price) });
  } catch {
    barcodeUrl = "";
  }

  const renderGeneral = () => <div className="inventory-form-grid">
    <Field label="Marca" required><input value={draft.brand} onChange={(event) => update("brand", event.target.value)} autoFocus /></Field>
    <Field label="Producto / modelo" required><input value={draft.model} onChange={(event) => update("model", event.target.value)} /></Field>
    <Field label="Sabor"><input value={draft.flavor} onChange={(event) => update("flavor", event.target.value)} /></Field>
    <Field label="Envase"><input value={draft.packaging} onChange={(event) => update("packaging", event.target.value)} /></Field>
    <div className="inventory-form-preview inventory-form-grid__wide">
      <span>Vista previa del nombre</span><strong>{productName || "El nombre se construirá con los datos del producto"}</strong>
    </div>
  </div>;

  const renderClassification = () => <div className="inventory-form-grid">
    <SelectField label="Categoría" required options={categories} selectedValue={draft.category} onChange={(value) => setDraft((current) => ({ ...current, category: value, subcategory: "" }))} searchable={categories.length > 8} searchPlaceholder="Buscar categoría…" />
    <SelectField label="Subcategoría" required options={subcategories} selectedValue={draft.subcategory} onChange={(value) => update("subcategory", value)} disabled={!draft.category} searchable={subcategories.length > 8} searchPlaceholder="Buscar subcategoría…" />
    <Field label="Formato" hint="Cantidad de la presentación"><input type="number" min="0" value={draft.format} onChange={(event) => update("format", event.target.value)} /></Field>
    <Field label="Unidad del formato"><input value={draft.format_unit} onChange={(event) => update("format_unit", event.target.value)} placeholder="g, kg, ml, L…" /></Field>
    <SelectField label="Unidad de venta" placeholder="Sin unidad" options={saleUnitOptions} selectedValue={draft.sale_unit} onChange={(value) => update("sale_unit", normalizeSaleUnit(value))} />
    <SelectField label="Ubicación" required options={Object.entries(availableLocations || {}).map(([value, label]) => ({ value, label }))} selectedValue={draft.location} onChange={(value) => update("location", value)} labelExtractor={(option) => option.label} valueExtractor={(option) => option.value} disabled={!isCreate} />
  </div>;

  const renderPricing = () => <div className="inventory-form-grid">
    <Field label={weighted ? "Costo por kg" : "Costo unitario"}><input type="number" min="0" value={draft.unit_cost} onChange={(event) => update("unit_cost", event.target.value)} /></Field>
    <Field label="Margen objetivo (%)"><input type="number" min="0" max="99.99" step="0.1" value={draft.margin} onChange={(event) => update("margin", event.target.value)} /></Field>
    <Field label={weighted ? "Precio de venta por kg" : "Precio de venta"}><input type="number" min="0" value={draft.unit_price} onChange={(event) => update("unit_price", event.target.value)} /></Field>
    {isCreate ? <><Field label={`Stock inicial (${unitLabel})`}><input type="number" min="0" step={stockStep} value={draft.qty} onChange={(event) => update("qty", event.target.value)} /></Field><Field label="Motivo del stock inicial" className="inventory-form-grid__wide"><input value={draft.adjustment_reason} onChange={(event) => update("adjustment_reason", event.target.value)} placeholder="Ej. Compra inicial proveedor" /></Field></> : null}
    <div className="inventory-price-summary inventory-form-grid__wide">
      <div><span>Costo {priceUnitLabel(draft.sale_unit)}</span><strong>{money(draft.unit_cost)}</strong></div>
      <div><span>Precio sugerido</span><strong>{money(suggestedPrice)}</strong></div>
      <div><span>Precio seleccionado {priceUnitLabel(draft.sale_unit)}</span><strong>{money(draft.unit_price)}</strong></div>
      <div><span>Margen real</span><strong>{actualMargin}%</strong></div>
      <div><span>Utilidad estimada</span><strong>{money(Number(draft.unit_price) - Number(draft.unit_cost))}</strong></div>
    </div>
  </div>;

  const renderInventory = () => <div className="inventory-stock-editor">
    <div className={`inventory-stock-editor__current${Number(product.stock_shortage_qty) > 0 ? " has-shortage" : ""}`}><span>Stock registrado</span><strong>{product.qty} {unitLabel}</strong>{Number(product.stock_shortage_qty) > 0 ? <small><span className="material-symbols-outlined" aria-hidden="true">warning</span> {Number(product.stock_shortage_qty).toLocaleString("es-CL")} {unitLabel} vendidos sin stock</small> : <small>Revisión {product.inventory_revision}</small>}</div>
    <div className="inventory-form-grid">
      <Field label={`Nueva cantidad física (${unitLabel})`} required><input type="number" min="0" step={stockStep} value={draft.qty} onChange={(event) => update("qty", event.target.value)} /></Field>
      <Field label="Diferencia"><output className={Number(draft.qty) - Number(product.qty) < 0 ? "is-negative" : ""}>{Number(draft.qty) - Number(product.qty) > 0 ? "+" : ""}{Number(draft.qty) - Number(product.qty)} {unitLabel}</output></Field>
      <Field label="Motivo del ajuste" required className="inventory-form-grid__wide"><textarea value={draft.adjustment_reason} onChange={(event) => update("adjustment_reason", event.target.value)} placeholder="Ej. Conteo físico de cierre" rows="3" /></Field>
    </div>
    <button type="button" className="inventory-primary-button" onClick={applyStockAdjustment} disabled={saving || (Number(draft.qty) === Number(product.qty) && Number(product.stock_shortage_qty || 0) === 0)}>Aplicar ajuste y resolver alerta</button>
  </div>;

  const renderBarcode = () => <div className="inventory-barcode-layout">
    <div className="inventory-barcode-controls">
      <SelectField label="Formato" options={BARCODE_TYPE_OPTIONS} selectedValue={draft.barcode_type} onChange={(value) => update("barcode_type", value)} labelExtractor={(option) => option.label} valueExtractor={(option) => option.value} />
      <Field label="Código" required hint={draft.barcode_type === "ean13" ? "No generes códigos comerciales fuera de un rango autorizado." : "Admite letras, números y guiones."}><input value={draft.code} onChange={(event) => update("code", event.target.value)} /></Field>
      <div className="inventory-inline-actions"><button type="button" className="inventory-secondary-button" onClick={generateBarcode} disabled={saving}><span className="material-symbols-outlined">auto_awesome</span> Generar</button><button type="button" className="inventory-secondary-button" onClick={checkBarcode} disabled={saving || !draft.code}><span className="material-symbols-outlined">verified</span> Validar</button></div>
      <div className="inventory-label-settings"><Field label="Copias"><input type="number" min="1" max="100" value={labelCopies} onChange={(event) => setLabelCopies(event.target.value)} /></Field><SelectField label="Tamaño" options={LABEL_SIZE_OPTIONS} selectedValue={labelSize} onChange={setLabelSize} labelExtractor={(option) => option.label} valueExtractor={(option) => option.value} /></div>
    </div>
    <div className="inventory-barcode-preview">
      <span>Vista previa de etiqueta</span>
      {barcodeUrl ? <img src={barcodeUrl} alt={`Código de barras ${draft.code}`} /> : <div className="inventory-barcode-preview__empty"><span className="material-symbols-outlined">barcode</span><p>Ingresa un código válido para ver la etiqueta.</p></div>}
      <div className="inventory-inline-actions"><button type="button" className="inventory-secondary-button" disabled={!barcodeUrl} onClick={() => downloadBarcodeSvg({ value: draft.code, type: draft.barcode_type, productName, price: money(draft.unit_price) }, `kakao-${draft.code}.svg`)}><span className="material-symbols-outlined">download</span> Descargar SVG</button><button type="button" className="inventory-primary-button" disabled={!barcodeUrl} onClick={() => printBarcodeLabels({ value: draft.code, type: draft.barcode_type, productName, price: money(draft.unit_price) }, { copies: labelCopies, labelSize })}><span className="material-symbols-outlined">print</span> Imprimir</button></div>
    </div>
  </div>;

  const renderReview = () => <div className="inventory-review-grid">
    <section><span>Producto</span><strong>{productName || "Incompleto"}</strong><p>{draft.code || "Sin código"}</p></section>
    <section><span>Clasificación</span><strong>{draft.category || "Sin categoría"}</strong><p>{draft.subcategory || "Sin subcategoría"}</p></section>
    <section><span>Ubicación</span><strong>{availableLocations?.[draft.location] || "Sin ubicación"}</strong><p>Stock inicial: {draft.qty} {unitLabel}</p></section>
    <section><span>Precio {priceUnitLabel(draft.sale_unit)}</span><strong>{money(draft.unit_price)}</strong><p>Costo {money(draft.unit_cost)} · Margen {draft.margin}%</p></section>
    {barcodeUrl ? <section className="inventory-review-grid__barcode"><img src={barcodeUrl} alt={`Etiqueta ${draft.code}`} /></section> : null}
  </div>;

  const renderHistory = () => {
    const timeline = [
      ...history.adjustments.map((item) => ({ ...item, kind: "adjustment", timestamp: item.created_at })),
      ...history.events.map((item) => ({ ...item, kind: "event", timestamp: item.created_at })),
    ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    if (historyLoading) return <div className="inventory-state"><span className="inventory-spinner" /> Cargando historial…</div>;
    if (!timeline.length) return <div className="inventory-state">Todavía no hay movimientos registrados para este producto.</div>;
    return <div className="inventory-history">{timeline.map((item) => <article key={`${item.kind}-${item.id}`}><span className="material-symbols-outlined">{item.kind === "adjustment" ? "sync_alt" : "history"}</span><div><strong>{item.kind === "adjustment" ? `${Number(item.delta) > 0 ? "+" : ""}${item.delta} ${unitLabel}` : item.event_type.replaceAll("_", " ")}</strong><p>{item.reason || item.responsible_username}</p><small>{new Date(item.timestamp).toLocaleString("es-CL")} · {item.responsible_username}</small></div></article>)}</div>;
  };

  const panels = { general: renderGeneral, classification: renderClassification, pricing: renderPricing, inventory: renderInventory, barcode: renderBarcode, review: renderReview, history: renderHistory };
  const ActivePanel = panels[activeTab];
  const activeTabIndex = tabs.findIndex(([key]) => key === activeTab);

  return (
    <div className="inventory-modal-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(dirty); }}>
      <section className="inventory-product-modal" role="dialog" aria-modal="true" aria-labelledby="inventory-product-modal-title" ref={modalRef}>
        <header className="inventory-product-modal__header">
          <div><span>{isCreate ? "Nuevo producto" : availableLocations?.[product.location] || "Inventario Kakao"}</span><h2 id="inventory-product-modal-title">{isCreate ? "Agregar producto" : product.description || productName}</h2>{!isCreate ? <p>{product.code} · Stock {product.qty} {unitLabel}</p> : <p>Completa las secciones y revisa el resultado antes de crear.</p>}</div>
          <button type="button" className="inventory-icon-button is-light" onClick={() => onClose(dirty)} disabled={saving} aria-label="Cerrar"><span className="material-symbols-outlined">close</span></button>
        </header>
        <div className="inventory-modal-tabs" role="tablist" aria-label="Secciones del producto">
          {tabs.map(([key, label, icon], index) => <button key={key} ref={index === 0 ? firstTabRef : null} type="button" role="tab" aria-selected={activeTab === key} aria-controls={`inventory-panel-${key}`} className={activeTab === key ? "is-active" : ""} onClick={() => setActiveTab(key)}><span className="material-symbols-outlined">{icon}</span>{label}</button>)}
        </div>
        <div className="inventory-product-modal__body" id={`inventory-panel-${activeTab}`} role="tabpanel">
          {message ? <InventoryToast type={message.type} message={message.text} onClose={() => setMessage(null)} /> : null}
          <ActivePanel />
        </div>
        <footer className="inventory-product-modal__footer">
          <span>{dirty ? "Cambios sin guardar" : isCreate ? "Completa los campos obligatorios" : "Producto actualizado"}</span>
          <div>
            <button type="button" className="inventory-secondary-button" onClick={() => onClose(dirty)} disabled={saving}>Cancelar</button>
            {isCreate && activeTabIndex > 0 ? <button type="button" className="inventory-secondary-button" onClick={() => setActiveTab(tabs[activeTabIndex - 1][0])} disabled={saving}>Atrás</button> : null}
            {isCreate && activeTab !== "review" ? <button type="button" className="inventory-primary-button" onClick={() => setActiveTab(tabs[Math.min(tabs.length - 1, activeTabIndex + 1)][0])} disabled={saving}>Continuar <span className="material-symbols-outlined">arrow_forward</span></button> : null}
            {isCreate && activeTab === "review" ? <button type="button" className="inventory-primary-button" onClick={saveProduct} disabled={saving}>{saving ? "Creando…" : "Crear producto"}</button> : null}
            {!isCreate && activeTab !== "inventory" && activeTab !== "history" ? <button type="button" className="inventory-primary-button" onClick={saveProduct} disabled={saving}>{saving ? "Guardando…" : "Guardar cambios"}</button> : null}
          </div>
        </footer>
      </section>
    </div>
  );
}

export default ProductEditorModal;
